import { SettingOutlined, EditOutlined } from '@ant-design/icons';
import { Button, Dropdown, Tag, Typography, Descriptions, Space, Alert, Tooltip } from 'antd';
import moment from 'moment';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Outlet } from 'react-router-dom';

import useAPIQuery from '../api/useAPIQuery';
import ErrorResult from '../components/ErrorResult';
import Page from '../components/Page';
import PageTitle from '../components/PageTitle';
import RequestJobPostingActionsModal from '../components/RequestJobPostingModal/RequestJobPostingActionsModal';
import usePermissionCheckerQuery from '../hooks/usePermissionCheckerQuery';
import useTypedNavigate from '../hooks/useTypedNavigate';
import useTypedParams from '../hooks/useTypedParams';
import { RequestJobPostingActionProps } from '../pages/RequestJobPostings';
import { RequestJobPostingId } from '../types';
import { calculateOpenDays, getRequestJobPostingFinishedAt } from '../utils';

const ShowRequestJobPostingNavigator = () => {
  const { t } = useTranslation();

  const navigate = useTypedNavigate();
  const { tenant, id } = useTypedParams({ tenant: String, id: RequestJobPostingId });
  const [selected, setSelected] = useState<RequestJobPostingActionProps>();

  const requestJobPosting = useAPIQuery(`/request-job-postings/${id}/`);

  const hasCreateAndEditRequestJobPostingsPermission = usePermissionCheckerQuery([
    'create_and_edit_request_job_postings',
  ]);

  return (
    <Page>
      {selected?.requestJobPosting && (
        <RequestJobPostingActionsModal
          {...selected}
          onOk={() => {
            requestJobPosting.refetch();
            setSelected({ ...selected, open: false });
          }}
          onCancel={() => setSelected({ ...selected, open: false })}
        />
      )}
      <ErrorResult error={requestJobPosting.error}>
        <PageTitle
          ghost={false}
          title={requestJobPosting.data?.name || '...'}
          extra={
            <Space direction="horizontal">
              <Tooltip
                title={
                  !hasCreateAndEditRequestJobPostingsPermission.data
                    ? t('Você não tem permissão.')
                    : ['CANCELED', 'REJECTED', 'APPROVED'].includes(
                        requestJobPosting.data?.status || ''
                      )
                    ? t(
                        'Essa vaga não pode ser cancelada, pois já foi aprovada, reprovada ou cancelada.'
                      )
                    : ''
                }
              >
                <Button
                  onClick={() => {
                    if (requestJobPosting.data) {
                      setSelected({
                        action: 'CANCELED',
                        requestJobPosting: requestJobPosting.data,
                        open: true,
                      });
                    }
                  }}
                  disabled={
                    !hasCreateAndEditRequestJobPostingsPermission.data ||
                    ['CANCELED', 'APPROVED', 'REJECTED'].includes(
                      requestJobPosting.data?.status || ''
                    )
                  }
                >
                  <Trans>Cancelar</Trans>
                </Button>
              </Tooltip>
              <Dropdown
                key="more"
                menu={{
                  onClick: ({ key }) => {
                    switch (key) {
                      case 'change':
                        navigate({
                          pathname: '/:tenant/request-job-postings/:id/change/',
                          params: { tenant, id },
                        });
                        break;
                    }
                  },
                  items: [
                    {
                      key: 'change',
                      icon: <EditOutlined />,
                      label: (
                        <Tooltip
                          title={
                            !hasCreateAndEditRequestJobPostingsPermission.data
                              ? t('Você não tem permissão.')
                              : ['CANCELED', 'REJECTED', 'APPROVED'].includes(
                                  requestJobPosting.data?.status || ''
                                )
                              ? t(
                                  'Essa vaga não pode ser editada, pois já foi aprovada, reprovada ou cancelada.'
                                )
                              : ''
                          }
                        >
                          <Trans>Editar requisição</Trans>
                        </Tooltip>
                      ),
                      disabled:
                        !hasCreateAndEditRequestJobPostingsPermission.data ||
                        ['CANCELED', 'APPROVED', 'REJECTED'].includes(
                          requestJobPosting.data?.status || ''
                        ),
                    },
                  ],
                }}
                placement="bottomRight"
              >
                <Button type="ghost" icon={<SettingOutlined />} />
              </Dropdown>
            </Space>
          }
        >
          <Descriptions size="small" column={6} layout="horizontal">
            <Descriptions.Item label={<Trans>Status</Trans>}>
              {requestJobPosting.data?.status === 'IN_PROGRESS' && (
                <Tag color="processing">
                  <Trans>Em andamento</Trans>
                </Tag>
              )}
              {requestJobPosting.data?.status === 'NOT_INITIATED' && (
                <Tag color="default">
                  <Trans>Não iniciada</Trans>
                </Tag>
              )}
              {requestJobPosting.data?.status === 'CANCELED' && (
                <Tag color="default">
                  <Trans>Cancelada</Trans>
                </Tag>
              )}
              {requestJobPosting.data?.status === 'REJECTED' && (
                <Tag color="error">
                  <Trans>Reprovada</Trans>
                </Tag>
              )}
              {requestJobPosting.data?.status === 'APPROVED' && (
                <Tag color="success">
                  <Trans>Concluída</Trans>
                </Tag>
              )}
            </Descriptions.Item>
            <Descriptions.Item label={t('Dias em aberto')}>
              {requestJobPosting.data?.created_at && (
                <Tag>
                  <Typography.Text>
                    {calculateOpenDays(
                      requestJobPosting.data?.created_at,
                      getRequestJobPostingFinishedAt(requestJobPosting.data)
                    )}{' '}
                    <Trans>dias</Trans>
                  </Typography.Text>
                </Tag>
              )}
            </Descriptions.Item>
            <Descriptions.Item
              label={t('Modelo de requisição')}
              span={2}
              labelStyle={{ whiteSpace: 'nowrap' }}
            >
              <Tag>
                <Typography.Text style={{ whiteSpace: 'break-spaces' }}>
                  {requestJobPosting.data?.template_name}
                </Typography.Text>
              </Tag>
            </Descriptions.Item>
            {requestJobPosting.data?.status === 'APPROVED' && (
              <Descriptions.Item contentStyle={{ justifyContent: 'flex-end' }}>
                <Alert
                  message={`${t('Concluída em')} ${moment(
                    requestJobPosting.data?.updated_at
                  ).format('DD/MM/YYYY - HH:mm [GMT]Z')}`}
                  type="success"
                />
              </Descriptions.Item>
            )}
          </Descriptions>
        </PageTitle>
      </ErrorResult>
      <Outlet />
    </Page>
  );
};

export default ShowRequestJobPostingNavigator;
